import * as React from 'react';

import { Button, Grid, Link, List, ListItem, ListItemIcon, ListItemText, Spacer, Typography } from '@emburse/ui-core';
import { BulletIcon, Icon } from '@emburse/ui-essentials';

import * as billing_plans from '@Shared/constants';
import { ICompanyModel } from '@Shared/models';

import { SignUpWorkflowFooter } from '../SignUpWorkflowFooter';

import * as style from './SignUpReferralScreen.styl';

interface ISignUpReferralScreenProps {
  nextStep: () => void;
  onBack: (navDirection: 'next' | 'back') => void;
  activateCompany: () => void;
  company: ICompanyModel;
  applyForEmburseCards: boolean;
  isRsmPartner?: boolean;
}

export const SignUpReferralScreen: React.FC<ISignUpReferralScreenProps> = ({
  nextStep,
  onBack,
  activateCompany,
  company,
  applyForEmburseCards,
  isRsmPartner
}) => {
  const supportTicketLink =
    window.whitelabelConfiguration?.supportLinks?.supportTicketLink || 'https://help.spend.emburse.com/hc/en-us/requests/new';
  let validBillingPlan = billing_plans.BILLING_PLAN_TO_DESCRIPTION_MAPPING[company.billing_plan];

  function handleNextStep() {
    if (applyForEmburseCards) {
      nextStep();
    } else {
      activateCompany();
    }
  }

  return (
    <Grid container direction="column" spacing={1} sx={{ height: '100%', maxWidth: '754px' }} wrap="nowrap" className={style.global}>
      <Grid item container className="layout" direction="column">
        <Grid item>
          <Typography variant="h4" style={{ alignSelf: 'flex-start' }}>
            Looks like you came from a representative!
          </Typography>
          <Spacer size={32} />
        </Grid>
        <Grid item>
          <Typography variant="body1" style={{ alignSelf: 'flex-start' }}>
            {isRsmPartner ? (
              <>
                Since you came from an Emburse Spend representative, your plan and implementation options have been pre-selected. You can go
                back to previous pages to edit your Company or Personal Information. Otherwise, you can move forward to start using Emburse
                Spend.
              </>
            ) : (
              <>
                Since you came from an Emburse Spend representative, you won't need to re-select some of your information. Review your
                previous selections or move forward to the next step. If the information below is not correct, please create a{' '}
                <Link href={supportTicketLink} target="_blank" rel="noopener">
                  support ticket
                </Link>
                .
              </>
            )}
          </Typography>
          <Spacer size={32} />
        </Grid>
        {!isRsmPartner && (
          <Grid item>
            <Typography variant="body1">You previously agreed to:</Typography>
            <List>
              {validBillingPlan && (
                <ListItem>
                  <ListItemIcon>
                    <Icon iconUrl={BulletIcon} />
                  </ListItemIcon>
                  <ListItemText primary={validBillingPlan} />
                </ListItem>
              )}
              <ListItem>
                <ListItemIcon>
                  <Icon iconUrl={BulletIcon} />
                </ListItemIcon>
                <ListItemText
                  primary={company.implementation_options === 'guided' ? 'Implementation assistance' : 'Self guided implementation'}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Icon iconUrl={BulletIcon} />
                </ListItemIcon>
                {applyForEmburseCards ? (
                  <ListItemText primary="Apply for Emburse Cards" />
                ) : (
                  <ListItemText primary="Emburse Cards application not requested at this point (skipped)" />
                )}
              </ListItem>
            </List>
            <Spacer size={32} />
          </Grid>
        )}
        <Grid item>
          <div className="flex">
            <Button
              variant="outlined"
              style={{ marginRight: 16, marginBottom: 16 }}
              onClick={() => onBack('back')}
              className="signup-referral-back"
            >
              Edit Personal Information
            </Button>
            <Button onClick={handleNextStep} style={{ marginBottom: 16 }} className="signup-referral-next">
              {applyForEmburseCards ? 'Continue to next step' : 'Start using Spend'}
            </Button>
          </div>
        </Grid>
      </Grid>
      <Grid item xs>
        <Spacer size={4} />
      </Grid>
      <Grid item container xs alignContent="flex-end">
        <SignUpWorkflowFooter showSupportLink={true} ariaLabel="referral-screen" />
      </Grid>
    </Grid>
  );
};
